import { useEffect, useRef } from "react";
import type { EvaluationEnvelope } from "@/types/evaluation";
import { exportEvaluationPdf } from "./evaluation-pdf";

type Props = {
  evaluation: EvaluationEnvelope | null;
  onClose: () => void;
};

const yn = (v: unknown) => (v === true || v === "si" || v === "Si" ? "Sí" : v === false || v === "no" ? "No" : String(v ?? ""));
const nested = (v: unknown): Record<string, unknown> => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});

export function EvaluationDetailDialog({ evaluation, onClose }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const open = evaluation !== null;

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  if (!evaluation) return <dialog ref={dialogRef} onCancel={onClose} onClose={onClose} className="detail-dialog" />;

  const adm = nested(evaluation.administrativos);
  const clin = nested(evaluation.clinicos);
  const gest = nested(evaluation.gestionEvaluador);
  const admAlerts = Array.isArray(evaluation.alertasAdministrativas) ? evaluation.alertasAdministrativas : [];
  const clinAlerts = Array.isArray(evaluation.alertasClinicas) ? evaluation.alertasClinicas : [];

  return (
    <dialog ref={dialogRef} onCancel={onClose} onClose={onClose} className="detail-dialog">
      <div className="detail-dialog-head">
        <div>
          <h3 className="detail-dialog-title">{evaluation.nombre || "Sin nombre"}</h3>
          <div className="detail-dialog-sub">
            doc {evaluation.doc || "—"} · {evaluation.cirugia || "sin cirugía"} · reg. {evaluation.fechaRegistro || "—"}
          </div>
        </div>
        <button type="button" className="detail-dialog-close" onClick={onClose} aria-label="Cerrar">✕</button>
      </div>

      <div className="detail-dialog-body">
        <section>
          <h4>Resultado</h4>
          <div className="detail-kv">
            <div><b>Administrativo:</b> {evaluation.estadoAdministrativo}</div>
            <div><b>Clínico:</b> {evaluation.resultadoClinico}</div>
            <div><b>Decisión final:</b> {evaluation.accionFinal}</div>
            <div><b>Evaluador:</b> {evaluation.evaluadorNombre || evaluation.evaluador}</div>
          </div>
        </section>

        <section>
          <h4>Signos vitales</h4>
          <div className="detail-kv">
            <div><b>PA:</b> {String(clin.ps ?? "?")}/{String(clin.pd ?? "?")} mmHg</div>
            <div><b>FC:</b> {String(clin.fc ?? "?")}</div>
            <div><b>SatO₂:</b> {String(clin.saturacion ?? "?")}</div>
            <div><b>Temp:</b> {String(clin.temp ?? "?")}°C</div>
            <div><b>Glicemia:</b> {String(clin.glicemia ?? "?")} mg/dL</div>
            <div><b>Blefaritis:</b> {String(clin.blefaritis ?? "?")}</div>
          </div>
        </section>

        <section>
          <h4>Historia</h4>
          <div className="detail-kv">
            <div><b>Antecedentes:</b> {String(evaluation.antecedentes || "—")}</div>
            <div><b>Medicamentos:</b> {String(evaluation.medicamentos || "—")}</div>
            <div><b>Alergias:</b> {String(evaluation.alergias || "—")}</div>
            <div><b>Cirugías previas:</b> {String(evaluation.cirugiasPrevias || "—")}</div>
          </div>
        </section>

        <section>
          <h4>Administrativos</h4>
          <div className="detail-kv">
            <div><b>Orden:</b> {yn(adm.orden)}</div>
            <div><b>Copago:</b> {yn(adm.copago)}</div>
            <div><b>Insumo:</b> {yn(adm.insumo)}</div>
            <div><b>Autorización:</b> {yn(adm.autorizacion)}</div>
            <div><b>Acompañante:</b> {yn(adm.acompanante)}</div>
            <div><b>Ayuno:</b> {yn(adm.ayuno)}</div>
          </div>
        </section>

        {(admAlerts.length > 0 || clinAlerts.length > 0) && (
          <section>
            <h4>Alertas registradas</h4>
            <div className="alerts-list">
              {admAlerts.map((a: unknown, i: number) => {
                const alt = a as { titulo?: string; severidad?: string; accion?: string };
                return (
                  <div key={`a-${i}`} className={`alert-item alert-item--${alt.severidad || "subsanable"}`}>
                    <div className="alert-item-title">📋 {alt.titulo}</div>
                    <div className="alert-item-meta">{alt.accion}</div>
                  </div>
                );
              })}
              {clinAlerts.map((a: unknown, i: number) => {
                const alt = a as { titulo?: string; clasificacion?: string; accion?: string };
                return (
                  <div key={`c-${i}`} className={`alert-item alert-item--${alt.clasificacion || "negociable"}`}>
                    <div className="alert-item-title">
                      {alt.clasificacion === "no-negociable" ? "🔴" : "🟡"} {alt.titulo}
                    </div>
                    <div className="alert-item-meta">{alt.accion}</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {(gest.requiereGestion === "si" || Boolean(gest.accionesEvaluador)) && (
          <section>
            <h4>Gestión del evaluador</h4>
            <div className="detail-kv">
              <div><b>Tipo:</b> {String(gest.tipoGestion || "—")}</div>
              <div><b>Acciones:</b> {String(gest.accionesEvaluador || "—")}</div>
              <div><b>Profesional consultado:</b> {String(gest.profesionalConsultado || "—")}</div>
              <div><b>Respuesta recibida:</b> {String(gest.respuestaRecibida || "—")}</div>
              <div><b>Evidencia cierre:</b> {String(gest.evidenciaCierre || "—")}</div>
            </div>
          </section>
        )}
      </div>

      <div className="detail-dialog-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cerrar</button>
        <button type="button" className="btn btn-primary" onClick={() => exportEvaluationPdf(evaluation)}>
          Descargar PDF
        </button>
      </div>
    </dialog>
  );
}
